import React, { useState, useEffect } from "react";
import { AlertTriangle, ShieldCheck, Lock, Loader } from "lucide-react";
import {
  fetchDisclosureAck,
  writeDisclosureAck,
  getCurrentUid,
} from "../../services/disclosureService";

/**
 * Disclaimer Gate (Risk Assessment)
 *
 * Renders BEFORE the Risk Assessment can start. The user must check ALL
 * six section checkboxes and click "I Agree — Begin Assessment" to
 * unlock the questions and the resulting report.
 *
 * Acknowledgment is persisted to Firestore (was previously localStorage)
 * under users/{uid}/settings/riskAssessmentDisclosureAck. This gives a
 * server-side audit trail of who acknowledged what, when.
 *
 * To force every user to re-acknowledge after a material change to the
 * disclaimer language, bump DISCLOSURE_VERSIONS.riskAssessment in
 * disclosureService.js.
 */

const KIND = "riskAssessment";
// Legacy localStorage key — used to migrate users from the old
// localStorage-only model. Once we see a localStorage record but no
// Firestore record, we backfill Firestore once.
const LEGACY_STORAGE_KEY = "arc-disclaimer-ack-v1";

const SECTIONS = [
  {
    id: "intro",
    title: "Educational use only — not investment advice",
    body:
      "The ARC FIRE Tracker and the risk assessment report it generates are " +
      "educational tools, not investment advice. ARCrypto is not a registered " +
      "investment advisor, broker-dealer, financial planner, or tax advisor. " +
      "Nothing in this report constitutes a recommendation to buy, sell, or " +
      "hold any specific asset.",
  },
  {
    id: "is",
    title: "What this report is",
    body:
      "Based on your answers, the report shows a hypothetical allocation " +
      "framework — a starting point for your own research and your own " +
      "conversations with licensed professionals. Different people with the " +
      "same risk score may reach different conclusions, and that's the point.",
  },
  {
    id: "isnot",
    title: "What this report is not",
    body:
      "It is not a personalized investment plan, a portfolio prescription, " +
      "or a guaranteed roadmap. It does not account for your full financial " +
      "picture — taxes, debts, dependents, employment risk, insurance, real " +
      "estate, estate planning, or your local regulatory environment.",
  },
  {
    id: "risk",
    title: "Risk of loss",
    body:
      "All investing involves risk, including loss of principal. Cryptocurrency " +
      "can lose 50–90% of its value in short periods and has historically " +
      "gone through multi-year drawdowns. Leveraged and on-chain DeFi " +
      "positions can lose 100% of their value in days. Equities and precious " +
      "metals can also experience significant declines. Some assets shown in " +
      "this app are illiquid and may not be sellable at the price displayed.",
  },
  {
    id: "past",
    title: "Past performance does not guarantee future results",
    body:
      "Any historical returns shown anywhere in this app — for managed " +
      "vaults, stock signals, precious metals, or benchmark indices — are " +
      "not a promise of future performance. Returns shown may include " +
      "hypothetical or backtested data, which has known limitations: it " +
      "does not reflect actual trading conditions, liquidity, slippage, " +
      "fees, taxes, or the emotional discipline required to hold a strategy " +
      "through drawdowns.",
  },
  {
    id: "yours",
    title: "Your decisions are yours",
    body:
      "You alone are responsible for your investment decisions. Before " +
      "acting on anything you see here, consult a licensed investment " +
      "advisor, tax professional, or attorney for advice specific to your " +
      "situation.",
  },
];

const DisclaimerGate = ({ onAcknowledge }) => {
  // Track which sections have been checked. Object keyed by section id.
  const [checked, setChecked] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [loadingAck, setLoadingAck] = useState(true);
  const [error, setError] = useState(null);

  // On mount, look for an existing acknowledgment. Order:
  //   1. Firestore record (proper, server-side)
  //   2. Legacy localStorage record — if found, backfill to Firestore
  //      once so the user doesn't lose their prior consent.
  useEffect(() => {
    let cancelled = false;
    const uid = getCurrentUid();
    if (!uid) {
      // No auth — shouldn't happen in normal flow, but if it does, force
      // the user to ack on this session.
      setLoadingAck(false);
      return;
    }

    (async () => {
      try {
        const fsRecord = await fetchDisclosureAck(uid, KIND);
        if (cancelled) return;
        if (fsRecord?.acknowledgedAt) {
          onAcknowledge?.(fsRecord);
          setLoadingAck(false);
          return;
        }

        // No Firestore record — check legacy localStorage for one-time backfill
        try {
          const raw = localStorage.getItem(LEGACY_STORAGE_KEY);
          if (raw) {
            const legacy = JSON.parse(raw);
            if (legacy?.acknowledgedAt) {
              const backfilled = await writeDisclosureAck(
                uid,
                KIND,
                Array.isArray(legacy.sections)
                  ? legacy.sections
                  : SECTIONS.map((s) => s.id)
              );
              // Remove legacy key so we don't repeat the migration.
              localStorage.removeItem(LEGACY_STORAGE_KEY);
              if (!cancelled) onAcknowledge?.(backfilled);
              setLoadingAck(false);
              return;
            }
          }
        } catch (_) {
          // localStorage failure is non-fatal — fall through to show gate
        }

        setLoadingAck(false);
      } catch (_) {
        if (!cancelled) setLoadingAck(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allChecked = SECTIONS.every((s) => checked[s.id]);

  const handleCheck = (id) => (e) => {
    setChecked((curr) => ({ ...curr, [id]: e.target.checked }));
  };

  const handleSubmit = async () => {
    if (!allChecked || submitting) return;
    const uid = getCurrentUid();
    if (!uid) {
      setError(
        "You need to be signed in to acknowledge. Please refresh and try again."
      );
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const record = await writeDisclosureAck(
        uid,
        KIND,
        SECTIONS.map((s) => s.id)
      );
      onAcknowledge?.(record);
    } catch (err) {
      setError(
        err.message || "Could not save your acknowledgment. Please try again."
      );
      setSubmitting(false);
    }
  };

  if (loadingAck) {
    return (
      <div className="bg-arc-bg py-12 px-4 rounded-lg">
        <div className="max-w-3xl mx-auto flex items-center justify-center gap-3 text-arc-muted">
          <Loader className="animate-spin" size={18} />
          Checking your disclosure status…
        </div>
      </div>
    );
  }

  return (
    <div className="bg-arc-bg py-8 px-4 rounded-lg">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-amber-500/10 border-2 border-amber-500/30 mb-4">
            <Lock size={28} className="text-amber-400" />
          </div>
          <p className="eyebrow text-amber-400 mb-2">
            BEFORE WE BEGIN  ·  REQUIRED
          </p>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-arc-ink mb-3">
            Read &amp; acknowledge the disclosure
          </h1>
          <p className="text-arc-muted max-w-2xl mx-auto leading-relaxed">
            Before you take the assessment, please read each of the six sections
            below and check the box next to each one to confirm you understand
            it. The assessment will unlock once all six are checked.
          </p>
        </div>

        {/* Six sections, each with its own checkbox */}
        <div className="space-y-3 mb-6">
          {SECTIONS.map((s, idx) => {
            const isChecked = !!checked[s.id];
            return (
              <label
                key={s.id}
                className={`block rounded-2xl border-2 p-4 sm:p-5 cursor-pointer transition-all ${
                  isChecked
                    ? "border-emerald-500/50 bg-emerald-500/5"
                    : "border-amber-500/30 bg-amber-500/5 hover:border-amber-500/50"
                }`}
              >
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={handleCheck(s.id)}
                    className="mt-1 w-5 h-5 flex-shrink-0 accent-arc-gold cursor-pointer"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 mb-1.5">
                      <span
                        className={`text-xs font-mono font-bold ${
                          isChecked ? "text-emerald-400" : "text-amber-400"
                        }`}
                      >
                        {idx + 1}/6
                      </span>
                      <h3 className="font-bold text-sm sm:text-base text-arc-ink">
                        {s.title}
                      </h3>
                    </div>
                    <p className="text-xs sm:text-sm text-arc-muted leading-relaxed">
                      {s.body}
                    </p>
                  </div>
                </div>
              </label>
            );
          })}
        </div>

        {/* Status + submit button */}
        <div className="rounded-2xl border border-arc-line bg-arc-surface p-5">
          <div className="flex items-center gap-3 mb-4">
            {allChecked ? (
              <>
                <ShieldCheck
                  size={20}
                  className="text-emerald-400 flex-shrink-0"
                />
                <p className="text-sm text-emerald-300">
                  All six sections acknowledged — you can now begin the
                  assessment.
                </p>
              </>
            ) : (
              <>
                <AlertTriangle
                  size={20}
                  className="text-amber-400 flex-shrink-0"
                />
                <p className="text-sm text-arc-muted">
                  <span className="font-semibold text-arc-ink">
                    {Object.values(checked).filter(Boolean).length} of 6
                  </span>{" "}
                  sections checked. Please review and check each section before
                  continuing.
                </p>
              </>
            )}
          </div>

          {error && (
            <div className="mb-3 p-3 rounded-lg border border-red-400/40 bg-red-500/10 text-xs sm:text-sm text-red-200">
              {error}
            </div>
          )}

          <button
            type="button"
            onClick={handleSubmit}
            disabled={!allChecked || submitting}
            className={`btn-tactile w-full px-6 py-3.5 rounded-xl text-sm sm:text-base font-semibold transition-all ${
              allChecked && !submitting
                ? "btn-gold cursor-pointer"
                : "bg-arc-line text-arc-dim cursor-not-allowed"
            }`}
          >
            {submitting
            ? "Saving…"
            : allChecked
              ? "I Agree — Begin Assessment"
              : "Check All Six Sections to Continue"}
          </button>

          <p className="text-[11px] text-arc-dim mt-3 text-center">
            Your acknowledgment is recorded server-side with today's date. You
            won't be asked again on any device unless the disclosure is
            updated.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DisclaimerGate;
